import { Component, ElementRef, TemplateRef, ViewChild } from '@angular/core';
import { GeneralService } from '../../services/general.service';
import { UserProfile } from 'src/app/interfaces/UserProfile';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CustomerService } from 'src/app/services/customer.service';
import DashboardSearchCriteria from 'src/app/interfaces/dashboardSearchCriteria';
import RedactCustomerRecords from 'src/app/interfaces/redactCustomerRecords';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    standalone: false
})
export class DashboardComponent {
    isLoading = true;
    isRedactRecordsLoading = true;
    companyType: string = '';
    userProfile: UserProfile | undefined;
    isOnProbation: boolean = false;
    probationStartDate?: string;
    probationEndDate?: string;
    probationModalDismissed: boolean = false;
    canRequestRecords: boolean = false;
    mustAcknowledgeAudit: boolean = false;
    isAuditAcknowledgeBypassed: boolean = false;
    isAuditAcknowledged: boolean = false;
    searchCriteria: DashboardSearchCriteria = { searchString: '' };
    isPrimary: boolean = false;
    redactRecords: RedactCustomerRecords | undefined;
    maskingChangedToOff: boolean = false;
    customerName: string = '';

    @ViewChild('ProbationModal')
    probationModal!: TemplateRef<any>;

    @ViewChild('ErrorSavingModal')
     errorSavingModal!: TemplateRef<any>;

    @ViewChild('ConfirmMaskingStatusChangeModal')
    confirmMaskingStatusChangeModal!: TemplateRef<any>;

    constructor (
        private authenticationService: AuthenticationService,
        public generalService: GeneralService,
        private router: Router,
        public modalService: NgbModal,
        private customerService: CustomerService
    ) {
        if (!authenticationService.isDeeplinkResolved()) {
            const deepLink = authenticationService.getDeepLink();
            if (deepLink) {
                window.location.href = deepLink;
            }
        }

        // CSRs are forbidden from accessing the dashboard
        const roles = authenticationService.getRoles();
        const isCsr = roles && roles.includes('csr_user');
        if (isCsr) {
            router.navigate(['/accessDenied']);
        }
    }

    send(searchCriteria: DashboardSearchCriteria) {
        this.searchCriteria = searchCriteria;
    }

    ngOnInit(): void {
        if (!this.userProfile) {
            this.userProfile = this.authenticationService.getUserProfile() as any;
        }
        this.getRedactRecords();

        if (this.userProfile && this.userProfile.customer && this.userProfile.customer.auditCustomerStatus?.toLocaleLowerCase() === 'probation'
            && this.userProfile!.customer!.auditPenalty!.penaltyStartDate && this.userProfile!.customer!.auditPenalty!.penaltyEndDate) {
            const start = DateTime.fromISO(this.userProfile!.customer!.auditPenalty!.penaltyStartDate.toString());
            const end = DateTime.fromISO(this.userProfile!.customer!.auditPenalty!.penaltyEndDate.toString());
            if (this.userProfile!.customer!.auditPenalty!.auditPenaltyActionId === 1 &&
                start <= DateTime.now() && end >= DateTime.now()
            ) {
                this.isOnProbation = true;
                this.probationStartDate = start.toLocaleString();
                this.probationEndDate = end.toLocaleString();
            }
        }

        this.canRequestRecords = (this.authenticationService.getRoles() !== null && this.authenticationService.getRoles().includes('mc_request'));
        this.isPrimary = (this.authenticationService.getRoles() !== null && this.authenticationService.getRoles().includes('primary'));

        this.setAuditAcknowledgementState();

        if (this.mustAcknowledgeAudit && !this.isAuditAcknowledgeBypassed && !this.isAuditAcknowledged) {
            this.router.navigateByUrl('/auditAcknowledgement');
        } 

        this.isLoading = false;
    }

    ngAfterViewInit() {
        if (this.isOnProbation && !(this.mustAcknowledgeAudit && !this.isAuditAcknowledgeBypassed && !this.isAuditAcknowledged)) {
            // wait 1 second then show probation modal
            setTimeout(() => {
                this.modalService.open(this.probationModal, { ariaLabelledBy: 'probationModalTitle' });
            }, 1000);
        }
    }

    setAuditAcknowledgementState() {
        const userProfile = this.authenticationService.getUserProfile() as any;
        this.isAuditAcknowledgeBypassed = this.authenticationService.isAuditAcknowledgeBypassed();
        this.isAuditAcknowledged = this.authenticationService.isAuditAcknowledged();
        if (userProfile && userProfile.customer && userProfile.customer.auditCustomer && userProfile.customer.auditCustomer !== null) {
            if (userProfile.customer.auditCustomer.AuditAcknowledgeDate
                && userProfile.customer.auditCustomer.AuditAcknowledgeDate === null
                && userProfile.customer.auditCustomer.auditCustomerStatusId === 1) {
                this.mustAcknowledgeAudit = true;
            } else {
                const auditAckDate = DateTime.fromISO(userProfile!.customer!.auditCustomer!.AuditAcknowledgeDate);
                const minDate = DateTime.fromFormat('01/01/2022', 'MM/dd/yyyy');
                const isAcknowledgedDate = auditAckDate.isValid && auditAckDate > minDate;
                const isInAudit = userProfile!.customer!.auditCustomer!.auditCustomerStatusId === 1;
                this.mustAcknowledgeAudit = isInAudit && !isAcknowledgedDate;
            }
        } else {
            this.mustAcknowledgeAudit = false;
        }
    }

    scrollToTop() {
        window.scrollTo(0, 0);
    }

    dismissProbationModal() {
        const username = this.authenticationService.getUserName()
        this.generalService.logActivity('audit_customer::acknowledge_probation',username, 
            [{ name: 'customer_id', value: this.userProfile?.customerId!}],
            [{ name: 'is_acknowledged', value: 'true'}]);
        this.probationModalDismissed = true;
        this.modalService.dismissAll();
    }

    setRecordMasking(maskRecords: boolean) {
        this.modalService.open(this.confirmMaskingStatusChangeModal, { ariaLabelledBy: 'confirmMaskingStatusChangeModalTitle' });
    }
    
    completedMaskingStatusChange() {
        const maskRecords = (this.redactRecords?.redactionOn === false || this.redactRecords?.redactionOn === undefined)  ? true : false;
        this.closeModal();
        this.isLoading = true;
        this.userProfile?.customerId;
        const savePromise = this.customerService.setRecordMasking(maskRecords, this.userProfile!.customerId);
        savePromise.subscribe((response: any) => {
            console.log(response)
            if (response) {
                this.redactRecords = response;
                if (this.redactRecords?.redactionOn === false) {
                    this.maskingChangedToOff = true;
                } else {
                    this.maskingChangedToOff = false;
                }
            } else {
                this.modalService.open(this.errorSavingModal, { ariaLabelledBy: 'errorSavingModalTitle' });
            }
            this.isLoading = false;
        });
    }

    closeModal() {
          this.modalService.dismissAll();
    }

    formatDateTimeWithHourMinute(date: Date | any) {
        if (date) {
            if (date.includes('.')) {
                date = date.substring(0, date.indexOf('.'));
            }
            const dateAsDate = DateTime.fromFormat(date, "yyyy-MM-dd'T'HH:mm:ss");
            const formattedDate = dateAsDate.toFormat('MM/dd/yyyy HH:mm');
            return formattedDate;
        }
        return '';
    }

    formatDateTime(date: Date | any) {
        if (date) {
            if (date.includes('.')) {
                date = date.substring(0, date.indexOf('.'));
            }
            const dateAsDate = DateTime.fromFormat(date, "yyyy-MM-dd'T'HH:mm:ss");
            const formattedDate = dateAsDate.toFormat('MM/dd/yyyy');
            return formattedDate;
        }
        return '';
    }

    getRedactRecords() {
        // TODO
        // Then translations 
        this.isRedactRecordsLoading = true;
        this.customerName = this.userProfile!.customer!.name!;
        const customerId = Number(this.userProfile!.customerId);
        const redactRecordsPromise = this.customerService.getRedactCustomerRecords(customerId);
        redactRecordsPromise.subscribe((result: any) => this.loadRedactRecords(result));
    }

    loadRedactRecords(redactRecords: RedactCustomerRecords | any) {
        if (redactRecords) {
            this.redactRecords = redactRecords as RedactCustomerRecords;
        }
        this.isRedactRecordsLoading = false;
    }

}
